import {
  InternalPayItemConnectionClassType,
  InternalPayItemConnectionClassTypeItem,
} from '@cims-online/shared'
import {
  InternalPayItemConnectionsQuery,
  InternalPayItemConnectionsQueryResult,
  useInternalPayItemConnectionsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { DefaultOptionType } from 'components/CIMSSelect/types'
import { useEffect, useState } from 'react'
import { useProjectContext } from 'context/ProjectContext'

type P2I =
  InternalPayItemConnectionsQuery['project']['projectToInternalConnections'][0]
export type ProjectToInternal = Pick<P2I, 'group' | 'order'> & {
  internal: InternalPayItemConnectionClassTypeItem
  id: string
}
type InternalConnectionsContext = Pick<
  InternalPayItemConnectionsQueryResult,
  'refetch'
> & {
  fetching: InternalPayItemConnectionsQueryResult['loading']
  data: ProjectToInternal[]
  groups: DefaultOptionType[]
}

const { Provider, useContextFn } =
  createStrictContext<InternalConnectionsContext>('InternalConnectionsContext')

export const useInternalConnectionsContext = useContextFn

const InternalConnectionsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const {
    data: response,
    loading: fetching,
    refetch,
  } = useInternalPayItemConnectionsQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId },
  })
  const groups = useOptions({
    items: response?.project?.payItemConnectionGroups,
    type: 'default',
    value: 'id',
    label: 'name',
    tooltip: 'description',
    sort: 'name',
  })
  const [data, setData] = useState<ProjectToInternal[]>([])
  useEffect(() => {
    setData(
      InternalPayItemConnectionClassType.values.reduce((data, internal) => {
        const id = internal.id
        const p2i = response?.project?.projectToInternalConnections?.find(
          ({ internalConnectionId }) => internalConnectionId === id
        )
        if (p2i) data.push({ ...p2i, internal, id })
        else data.push({ order: 0, internal, id, group: null })
        return data
      }, Array<ProjectToInternal>())
    )
  }, [response?.project?.projectToInternalConnections])
  return (
    <Provider value={{ data, fetching, refetch, groups }}>{children}</Provider>
  )
}

export default InternalConnectionsProvider
